/**
 * Two-step sign-in (docs/specs/cloud/mfa.md): a code from an authenticator app (TOTP, Supabase Auth MFA) after the
 * email link or Google or Microsoft sign-in, in Teslyn Cloud and Teslyn Field. The database decides what a session
 * may change (20261022000000_mfa.sql, `mfa_satisfied`); this file has the sign-in steps both apps show, the
 * per-tenant setting's names and the words.
 */

/** Who an organisation requires to use two-step sign-in (`tenant_security.mfa_required`). */
export const MFA_LEVELS = ['off', 'admins', 'staff'] as const;
export type MfaLevel = (typeof MFA_LEVELS)[number];

export const MFA_LEVEL_LABEL: Record<MfaLevel, string> = {
  off: 'Not required (each person may turn it on)',
  admins: 'Required for tenant admins',
  staff: 'Required for all staff',
};

export function isMfaLevel(v: unknown): v is MfaLevel {
  return typeof v === 'string' && (MFA_LEVELS as readonly string[]).includes(v);
}

/** What the session needs after the first step of signing in. */
export type MfaStep =
  /** Nothing more: no app set up and none required, or the code was entered. */
  | 'none'
  /** The person has an authenticator app: ask for its code. */
  | 'verify'
  /** An organisation (or Braynewave's console) requires two-step sign-in and no app is set up yet. */
  | 'enrol';

export interface MfaFacts {
  /** The session's assurance level (`aal` of the JWT). */
  readonly aal: 'aal1' | 'aal2';
  /** The person has a verified authenticator app. */
  readonly hasFactor: boolean;
  /** An organisation of theirs requires it, or they are working in Braynewave's console. */
  readonly required: boolean;
}

/**
 * The step to show. Someone who has set up an app always uses it (the database refuses their sensitive changes
 * otherwise); someone required to have one sets it up before going on.
 */
export function mfaStep(f: MfaFacts): MfaStep {
  if (f.aal === 'aal2') return 'none';
  if (f.hasFactor) return 'verify';
  return f.required ? 'enrol' : 'none';
}

/** The six digits typed, without spaces; null when it is not a six-digit code. */
export function totpCode(input: string): string | null {
  const v = input.replace(/[\s-]/g, '');
  return /^\d{6}$/.test(v) ? v : null;
}

/** Words for Supabase Auth's answers to a code (wrong, expired, too many tries). */
export function mfaCodeError(message: string): string {
  if (/invalid|incorrect|verification failed/i.test(message))
    return 'That code is not right. Check the time on your phone and enter the newest code.';
  if (/expired|challenge/i.test(message)) return 'The code expired. Enter the newest code from the app.';
  if (/rate|too many/i.test(message)) return 'Too many tries. Wait a minute, then enter the newest code.';
  return `Two-step sign-in failed: ${message}`;
}

/** Why mfa-reset refused (public.mfa_reset_check reasons), in words. */
export const MFA_RESET_REFUSAL: Record<string, string> = {
  needs_aal2: 'Enter the code from your authenticator app first (two-step sign-in).',
  self: 'Remove your own authenticator app on your Security page.',
  not_admin:
    'Only a tenant admin of every organisation this person belongs to, or Braynewave, can reset their two-step sign-in.',
  platform_admin: "Only Braynewave can reset a Braynewave admin's two-step sign-in.",
  not_found: 'This person has no Teslyn account.',
  bad_request: 'The request is incomplete.',
};
